import { Capacitor } from '@capacitor/core'
import { ArrowUp, RefreshCw } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { useForceUpdate } from '@/lib/force-update'

// Native store listing for the current platform (see .env.example) — e.g.
// https://apps.apple.com/app/idXXXXXXXXXX or a Play Store
// https://play.google.com/store/apps/details?id=app.pronteer link.
const STORE_URL = (
  Capacitor.getPlatform() === 'android'
    ? import.meta.env.VITE_PLAY_STORE_URL
    : import.meta.env.VITE_APP_STORE_URL
) as string | undefined

const isNative = Capacitor.isNativePlatform()

function handleAction() {
  if (isNative) {
    if (STORE_URL) window.open(STORE_URL, '_blank', 'noopener,noreferrer')
  } else {
    // Web (ops.pronteer.com): reload to fetch the latest deployed bundle and
    // shed any stale PWA/service-worker cache.
    window.location.reload()
  }
}

/**
 * Full-screen blocking gate shown when the API reports the client is too old
 * (CLIENT_OUTDATED). There is no dismiss affordance — the only action is to
 * update (App Store on native) or reload (web). Rendered above the whole app;
 * mounts only once `required` is set.
 */
export function UpdateRequiredScreen() {
  const { required } = useForceUpdate()

  if (!required) return null

  const Icon = isNative ? ArrowUp : RefreshCw
  // On native we can only act if we know where to send the user.
  const canAct = isNative ? Boolean(STORE_URL) : true

  return (
    <div className="fixed inset-0 z-9999 flex flex-col items-center justify-center gap-6 bg-surface-L0 px-6 pb-[var(--safe-bottom)] pt-[var(--safe-top)]">
      <div className="flex size-16 items-center justify-center rounded-full bg-surface-L2 text-content-primary-bold">
        <Icon className="size-8" />
      </div>
      <div className="text-center">
        <h1 className="text-lg font-semibold text-content-primary-bold">Update required</h1>
        <p className="mt-1 text-sm text-content-primary-subtle">
          {isNative
            ? 'A newer version of Pronteer is available. Please update to continue.'
            : 'A newer version of Pronteer is available. Please reload to continue.'}
        </p>
      </div>
      {canAct && (
        <Button variant="primary" size="lg" onClick={handleAction}>
          {isNative ? 'Update Pronteer' : 'Reload'}
        </Button>
      )}
    </div>
  )
}
